/**
 * Vezme DOM uzel (náhled dokumentu) a vrátí kompletní self-contained HTML
 * stránku připravenou pro Playwright PDF rendering (a pro e-mailové PDF).
 *
 * Důležité: backend Playwright renderuje proti `http://pdf.local/`, kam
 * nedosáhne na `/v2/assets/index.css`. Proto NESMÍME spoléhat na `<link>`
 * (v produkci je CSS externí jediný soubor) — místo toho serializujeme
 * skutečná CSS pravidla z `document.styleSheets` a vložíme je jako `<style>`.
 * V devu jsou styly v `<style>` (Vite), v produkci v same-origin `<link>` —
 * obojí je čitelné přes `cssRules`. Cross-origin (Google Fonts) `cssRules`
 * vyhodí výjimku → takový zdroj přeskočíme, ale ponecháme jeho `<link>`.
 */

/** Posbírá všechna čitelná CSS pravidla (same-origin bundle i dev <style>). */
function collectInlineCss(): string {
  let css = '';
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList | null = null;
    try {
      rules = sheet.cssRules;
    } catch {
      // cross-origin (typicky Google Fonts) — pravidla nečitelná, přeskoč
      rules = null;
    }
    if (!rules) continue;
    for (const rule of Array.from(rules)) css += rule.cssText + '\n';
  }
  return css;
}

/**
 * Stáhne same-origin obrázky (např. sken přílohy trauma plánu z `/trauma-plan/…`)
 * a převede je na `data:` URL, aby bylo výsledné HTML self-contained.
 *
 * Bez toho je výstup rozbitý: backend renderuje PDF přes Playwright proti
 * `http://pdf.local/`, kam relativní `/…` cesty nedosáhnou, a v e-mailu u
 * příjemce relativní URL také nikdy nefungují. Cross-origin obrázky a už
 * hotové `data:` URL (logo, podpisy, QR) ponecháme beze změny.
 */
async function inlineSameOriginImages(root: HTMLElement): Promise<void> {
  const imgs = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    imgs.map(async (img) => {
      const src = img.getAttribute('src');
      if (!src || src.startsWith('data:')) return;
      let url: URL;
      try {
        url = new URL(src, location.href);
      } catch {
        return;
      }
      if (url.origin !== location.origin) return; // cross-origin nech být
      try {
        const res = await fetch(url.href);
        if (!res.ok) return;
        const blob = await res.blob();
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result as string);
          fr.onerror = () => reject(fr.error);
          fr.readAsDataURL(blob);
        });
        img.setAttribute('src', dataUrl);
      } catch {
        // ponech původní src — radši rozbitý obrázek než selhání celého exportu
      }
    }),
  );
}

/**
 * Nahradí formulářové prvky (`input`, `textarea`, `select`) a plátna (`canvas`)
 * statickým textem / obrázkem.
 *
 * Bez toho se dokument do PDF nevytiskne celý:
 *  – `textarea` má výšku danou atributem `rows` (a dopočítanou pro šířku
 *    obrazovky). Papír v PDF je užší, text se přeleje na víc řádků a přebytek
 *    se OŘÍZNE — typicky dlouhé sekce objednávky (Povinnosti zhotovitele,
 *    Záruční podmínky, Smluvní pokuty).
 *  – `input` v úzké buňce tabulky (kontaktní osoby) hodnotu ořízne, takže se
 *    vytiskne jen `marek.kosaristan@kvs-stave` místo celého e-mailu.
 *  – `cloneNode(true)` kopíruje jen ATRIBUT `value`, ne aktuální hodnotu
 *    z DOM → do PDF by šel původní, needitovaný text.
 *  – `canvas` se klonuje PRÁZDNÝ (bitmapa se nekopíruje) → podpis (nakreslený
 *    i nahraný z obrázku) se do PDF vůbec nedostal.
 *
 * Náhrady si berou vzhled (font, barvu, zarovnání) z původního prvku, ale bez
 * omezení výšky a s povoleným zalomením, takže se vytiskne vždy celý obsah.
 */
const CONTROL_SELECTOR = 'input, textarea, select, canvas';

function flattenFormControls(source: HTMLElement, clone: HTMLElement): void {
  const originals = Array.from(source.querySelectorAll<HTMLElement>(CONTROL_SELECTOR));
  const clones = Array.from(clone.querySelectorAll<HTMLElement>(CONTROL_SELECTOR));
  // Klon je přesná kopie, takže pořadí sedí 1:1. Kdyby ne, radši nesaháme na nic.
  if (originals.length !== clones.length) return;
  originals.forEach((orig, i) => {
    const replacement = staticNodeFor(orig);
    if (replacement) clones[i].replaceWith(replacement);
  });
}

/** Statická náhrada jednoho prvku, nebo `null` když se nemá nahrazovat. */
function staticNodeFor(el: HTMLElement): HTMLElement | null {
  if (el instanceof HTMLCanvasElement) {
    let dataUrl = '';
    try {
      dataUrl = el.toDataURL('image/png');
    } catch {
      return null; // „tainted" plátno — nech původní (prázdný) canvas
    }
    if (!dataUrl.startsWith('data:image')) return null;
    const img = document.createElement('img');
    img.src = dataUrl;
    img.alt = '';
    img.className = el.className; // .canvas → width/height 100 % v podpisovém boxu
    img.style.objectFit = 'contain';
    return img;
  }

  let text: string | null = null;
  if (el instanceof HTMLTextAreaElement) {
    text = el.value;
  } else if (el instanceof HTMLSelectElement) {
    text = el.selectedOptions[0]?.textContent ?? '';
  } else if (el instanceof HTMLInputElement) {
    if (el.type === 'checkbox' || el.type === 'radio') text = el.checked ? '☑' : '☐';
    else if (['file', 'hidden', 'button', 'submit', 'reset', 'image'].includes(el.type)) return null;
    else text = el.value;
  }
  if (text === null) return null;

  const cs = getComputedStyle(el);
  const box = document.createElement('div');
  box.className = el.className;
  box.textContent = text;
  box.style.fontFamily = cs.fontFamily;
  box.style.fontSize = cs.fontSize;
  box.style.fontWeight = cs.fontWeight;
  box.style.fontStyle = cs.fontStyle;
  box.style.lineHeight = cs.lineHeight;
  box.style.letterSpacing = cs.letterSpacing;
  box.style.color = cs.color;
  box.style.textAlign = cs.textAlign;
  box.style.padding = cs.padding;
  box.style.display = 'block';
  box.style.boxSizing = 'border-box';
  box.style.maxWidth = '100%';
  box.style.height = 'auto';
  box.style.minHeight = el instanceof HTMLTextAreaElement ? '0' : '1em';
  box.style.overflow = 'visible';
  box.style.whiteSpace = 'pre-wrap';
  box.style.overflowWrap = 'anywhere';
  box.style.border = '0';
  box.style.background = 'transparent';
  return box;
}

/** Jen cross-origin stylesheet linky (např. Google Fonts) — Playwright je načte z CDN. */
function crossOriginLinkTags(): string {
  return Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
    .filter((l) => {
      try {
        return new URL((l as HTMLLinkElement).href).origin !== location.origin;
      } catch {
        return false;
      }
    })
    .map((l) => l.outerHTML)
    .join('\n');
}

export async function buildDocHtml(el: HTMLElement | null, lang: string = 'cs'): Promise<string> {
  if (!el) return '';

  // Klon, ať neměníme živý DOM; lazy obrázky převedeme na eager (Playwright je
  // jinak nenačte, jsou mimo viewport — např. příloha trauma plánu).
  const clone = el.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('img[loading="lazy"]').forEach((img) => {
    img.setAttribute('loading', 'eager');
  });

  // Formulářové prvky a podpisová plátna → statický text / obrázek.
  // Musí proběhnout PŘED `inlineSameOriginImages`, aby se případné nové <img>
  // (z canvasu) už braly jako hotové data: URL.
  flattenFormControls(el, clone);

  // Same-origin obrázky vložíme jako data: URL → self-contained HTML pro PDF/e-mail.
  await inlineSameOriginImages(clone);

  const inlineCss = collectInlineCss();
  const linkTags = crossOriginLinkTags();

  return `<!DOCTYPE html>
<html lang="${lang}"${document.documentElement.dataset.docWhite ? ' data-doc-white="1"' : ''}>
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
${linkTags}
<style>
${inlineCss}
</style>
<style>
  /* PDF normalizace — zbavit se scrollů, tmavé barvy preview převést na tisk */
  html, body { background: #ffffff !important; color: #12325a; margin: 0; padding: 0; }
  * { color-adjust: exact; -webkit-print-color-adjust: exact; }
  /* Okraje papíru přidává Playwright (10 mm nahoře/dole, 8 mm po stranách) —
     další padding tady by je zdvojil a zbytečně ubíral místo. */
  body { padding: 0; font-family: 'Inter', system-ui, -apple-system, sans-serif; }
</style>
</head>
<body>${clone.outerHTML}</body>
</html>`;
}
